import axios from "axios";
import jwtDecode from "jwt-decode";
import { MOCK_USER } from "@/dev/fixtures";

const DEV_BYPASS_TOKEN = "dev-bypass-mock-token";

function isDevBypassActive() {
  return (
    process.env.NODE_ENV === "development" &&
    localStorage.getItem("devBypassAuth") === "true"
  );
}

function getInitialState() {
  if (isDevBypassActive()) {
    return {
      isAuthenticated: true,
      user: MOCK_USER,
      isDevBypass: true,
    };
  }
  const token = localStorage.getItem("token");
  const storedUser = localStorage.getItem("user");
  let user = null;
  try {
    user =
      storedUser && storedUser !== "undefined"
        ? JSON.parse(storedUser)
        : null;
  } catch (error) {
    console.error("Error parsing user from localStorage:", error);
  }
  return {
    isAuthenticated: !!token,
    user,
    isDevBypass: false,
  };
}

const state = {
  ...getInitialState(),
};

const mutations = {
  SET_AUTH(state, { user, token }) {
    state.isAuthenticated = true;
    state.user = user;
    state.isDevBypass = token === DEV_BYPASS_TOKEN;
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(user));
  },
  LOGOUT(state) {
    state.isAuthenticated = false;
    state.user = null;
    state.isDevBypass = false;
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("devBypassAuth");
  },
  PATCH_USER(state, patch) {
    if (!state.user) return;
    state.user = { ...state.user, ...patch };
    localStorage.setItem("user", JSON.stringify(state.user));
  },
  /** Enable dev bypass: no real login, use mock user (development only). */
  ENABLE_DEV_BYPASS(state) {
    if (process.env.NODE_ENV !== "development") return;
    state.isAuthenticated = true;
    state.user = { ...MOCK_USER };
    state.isDevBypass = true;
    localStorage.setItem("devBypassAuth", "true");
    localStorage.setItem("token", DEV_BYPASS_TOKEN);
    localStorage.setItem("user", JSON.stringify(state.user));
  },
  /** Disable dev bypass and clear mock auth. */
  DISABLE_DEV_BYPASS(state) {
    state.isDevBypass = false;
    state.isAuthenticated = false;
    state.user = null;
    localStorage.removeItem("devBypassAuth");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  },
};

const actions = {
  async loginUser({ commit }, credentials) {
    // eslint-disable-next-line no-useless-catch
    try {
      const response = await axios.post(
        `${process.env.VUE_APP_API_URL}/api/auth/login`,
        credentials
      );
      const { token, user } = response.data;

      // Prefer the explicit user profile returned by the backend (includes firstName, lastName, etc.)
      if (user && token) {
        commit("SET_AUTH", { user, token });
      } else if (token) {
        // Fallback to decoded token if user payload is ever missing
        try {
          const decodedToken = jwtDecode(token);
          console.log("Decoded Token:", decodedToken);
          commit("SET_AUTH", { user: decodedToken, token });
        } catch (error) {
          console.error("Failed to decode JWT:", error);
        }
      }
    } catch (error) {
      throw error;
    }
  },
  logout({ commit }) {
    commit("LOGOUT");
  },

  /** Refresh JWT via POST /api/auth/refresh. Uses plain axios to avoid circular dependency with axios instance. */
  async refreshToken({ commit, state }) {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("No token to refresh");

    const response = await axios.post(
      `${process.env.VUE_APP_API_URL}/api/auth/refresh`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const newToken = response.data?.token;
    if (!newToken) throw new Error("No token in refresh response");

    const user = state.user || (() => {
      try {
        const raw = localStorage.getItem("user");
        return raw && raw !== "undefined" ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    })();
    commit("SET_AUTH", { user: user || {}, token: newToken });
    return newToken;
  },
};

function readRoleFromToken() {
  const token = localStorage.getItem("token");
  if (!token || token === DEV_BYPASS_TOKEN) return null;
  try {
    return jwtDecode(token).role || null;
  } catch {
    return null;
  }
}

const getters = {
  isAuthenticated: (state) => state.isAuthenticated,
  user: (state) => state.user,
  isDevBypass: (state) => !!state.isDevBypass,
  isAdmin: (state) => {
    if (state.user?.isAdmin === true) return true;
    if (state.user?.role === "admin") return true;
    return readRoleFromToken() === "admin";
  },
  /** Unix timestamp (ms) when token expires, or null */
  tokenExpiresAt() {
    const token = localStorage.getItem("token");
    if (!token || token === DEV_BYPASS_TOKEN) return Date.now() + 7 * 24 * 60 * 60 * 1000;
    try {
      const decoded = jwtDecode(token);
      return decoded.exp ? decoded.exp * 1000 : null;
    } catch {
      return null;
    }
  },
  /** True if token expires within 24 hours */
  isTokenExpiringSoon(_state, getters) {
    if (_state.isDevBypass) return false;
    const exp = getters.tokenExpiresAt;
    if (!exp) return false;
    const oneDayMs = 24 * 60 * 60 * 1000;
    return exp - Date.now() < oneDayMs;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
